import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';

import { Button } from 'react-bootstrap';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import Loader from 'components/Loader';
import { ProjectMetadataTags } from 'models';
import { intlGet } from 'utils/intlGet';
import { LayoutDetailsProps } from './models';
import styles from './styles.module.scss';

const LAYOUT_DETAILS_TAGS = 'LayoutDetails.Tags';
const LAYOUT_DETAILS_TAGS_BUTTONS = 'LayoutDetails.Tags.Buttons';

const LayoutDetails: React.FunctionComponent<LayoutDetailsProps> = ({
  options,
  hideModal,
  modalWindowId,
  updateReusableLayoutTags,
}) => {
  const onClose = (): void => { hideModal(modalWindowId); };

  // Extract document properties
  const document = options.get('document');
  const metadata = options.get('metadata');
  const isSavingProcess = options.get('isSaving') ?? false;
  const classification = document.get('classification');
  const number = document.get('number');
  const status = document.get('status');
  const product = document.get('product');
  const thumbnailUrl = document.get('_thumbnailUrl');
  const documentId = document.get('documentId');
  const documentName = document.get('name') ?? '';

  const image = React.createRef<HTMLImageElement>();
  const [fullSized, setFullSized] = useState(false);
  const toggleFullSizedState = (): void => setFullSized(prev => !prev);
  const productName = _.first(product.toJS());

  const [isEditMode, setIsEditMode] = React.useState(false);

  // Consolidated state for all form data
  const [formData, setFormData] = React.useState<ProjectMetadataTags>({
    name: documentName,
    audienceType: [],
    channelType: [],
    contentType: [],
  });

  // Original data for reset functionality
  const [originalData, setOriginalData] = React.useState<ProjectMetadataTags>({
    name: documentName,
    audienceType: [],
    channelType: [],
    contentType: [],
  });

  // Load existing tags when component mounts
  React.useEffect(() => {
    if (metadata) {
      const tags = metadata.toJS() as ProjectMetadataTags;
      const cleanedTags = {
        name: tags.name ?? documentName,
        audienceType: tags.audienceType || [],
        channelType: tags.channelType || [],
        contentType: tags.contentType || [],
      };
      setFormData(cleanedTags);
      setOriginalData(cleanedTags);
    }
  }, [metadata, documentName]);

  // Add validation for name input
  const hasNameError = isEditMode && (!formData.name || formData.name.trim() === '');

  // Helper function to update form data
  const updateFormField = <K extends keyof ProjectMetadataTags>(
    field: K,
    value: ProjectMetadataTags[K],
  ): void => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = (): void => {
    setFormData(originalData);
    setIsEditMode(false);
  };

  const handleEdit = (): void => {
    setIsEditMode(true);
  };

  const handleSave = (): void => {
    updateReusableLayoutTags(modalWindowId, documentId, formData);
    setIsEditMode(false);
  };

  const isSaveButtonDisabled = hasNameError;

  return (
    <div className={styles.LayoutDetails}>
      {isSavingProcess && (
        <div className={styles.loadingOverlay}>
          <Loader />
        </div>
      )}
      <header>
        <div className={styles.info}>
          <div className={styles.title}>{originalData.name}</div>
          {!isEditMode &&
          <Icon
            type={IconType.EDIT}
            size="xs"
            color="primary"
            onClick={handleEdit}
            title={intlGet(LAYOUT_DETAILS_TAGS, 'Name.EditTooltip')}
          />}
        </div>
        <Icon className={styles.closeIcon} onClick={onClose} type={IconType.CLOSE} size="sm-md" color="primary" />
      </header>
      <section>
        <div className={styles.metadata}>
          {status && <span className={styles.status}>{String(status).toUpperCase()}</span>}
          <span className={styles.number}>{number}</span>
          {productName && <span className={styles.valueWithSeparator}>{productName}</span>}
          {classification && <span className={styles.valueWithSeparator}>{classification}</span>}
        </div>
        {isEditMode && <div className={styles.tagsSectionRow}>
          <div className={styles.tagsSectionRowTagName}>
            <span>{intlGet(LAYOUT_DETAILS_TAGS, 'Name')}</span>
          </div>
          <div className={styles.tagsSectionRowTagValue}>
            <input
              className={classNames(styles.tagsSectionRowTagValueNameInput,
                {
                  [styles.tagsSectionRowTagValueNameInput__error]: hasNameError,
                },
              )}
              type='text'
              value={formData.name}
              onChange={(e): void => updateFormField('name', e.target.value)}
            />

            {hasNameError && (
              <div className={styles.tagsSectionRowTagValueNameInput__errorText}>
                <span>{intlGet(LAYOUT_DETAILS_TAGS, 'Name.Error')}</span>
              </div>
            )}
          </div>
        </div>}
        {isEditMode &&
          <div className={styles.tagsSectionActions}>
            <Button onClick={handleCancel} variant="secondary">{intlGet(LAYOUT_DETAILS_TAGS_BUTTONS, 'Cancel')}</Button>
            <Button
              onClick={handleSave}
              variant="primary"
              disabled={isSaveButtonDisabled}
            >
              {intlGet(LAYOUT_DETAILS_TAGS_BUTTONS, 'Save')}
            </Button>
          </div>}
        <div className={styles.preview}>
          {
            thumbnailUrl
              ?
              <img
                ref={image}
                className={classNames(styles.image, { [styles.fullSize]: fullSized })}
                src={thumbnailUrl}
                onClick={toggleFullSizedState}
              />
              :
              <div className={styles.icon}>
                <Icon type={IconType.IMAGE} size="xxl" color="grey" />
                <span className={styles.iconText}>{intlGet('ProjectPanel.ImageNotAvailable', 'Preview')}</span>
              </div>
          }
        </div>
      </section>
    </div>
  );
};

export default LayoutDetails;
